import React from 'react';
import { PropsOf } from '@headlessui/react/dist/types';
import { twMerge } from 'tailwind-merge';
import { HiArrowsUpDown, HiChevronUp, HiChevronDown } from 'react-icons/hi2';
import dayjs from 'dayjs';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from './BaseTable';

export interface SortConfig {
  column: string;
  direction: 'asc' | 'desc';
}

export interface FinancialColumnConfig {
  id: string;
  label: React.ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  headerClassName?: string;
}

interface FinancialTableProps<T> {
  columns: FinancialColumnConfig[];
  rows: T[];
  sortConfig?: SortConfig;
  onSortChange?: (config: SortConfig) => void;
  renderRow: (row: T, index: number) => React.ReactNode;
  className?: string;
  showSpacerRow?: boolean;
}

/**
 * FinancialTable is a standardized table component for financial pages.
 * It provides consistent styling, sortable headers, and standardized row behavior.
 */
export function FinancialTable<T>({
  columns,
  rows,
  sortConfig,
  onSortChange,
  renderRow,
  className,
  showSpacerRow = false,
}: FinancialTableProps<T>): JSX.Element {
  const handleSort = (columnId: string) => {
    if (!onSortChange || !sortConfig) return;

    onSortChange({
      column: columnId,
      direction:
        sortConfig.column === columnId && sortConfig.direction === 'desc' ? 'asc' : 'desc',
    });
  };

  return (
    <div className="relative w-full overflow-x-auto rounded-lg border border-secondary-200 overflow-hidden">
      <BaseTable className={twMerge('min-w-full', className)}>
        <BaseTableHeader>
          <BaseTableHeaderRow className="[&_th:first-child]:!rounded-tl-lg [&_th:first-child]:!rounded-bl-none [&_th:first-child]:!rounded-l-none [&_th:last-child]:!rounded-tr-lg [&_th:last-child]:!rounded-br-none [&_th:last-child]:!rounded-r-none [&_th]:!rounded-none">
            {columns.map((column) => {
              if (column.sortable && sortConfig && onSortChange) {
                return (
                  <SortableHeaderCell
                    key={column.id}
                    column={column.id}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    className={twMerge('px-3 py-3', column.headerClassName)}
                    style={{ textAlign: column.align || 'left' }}>
                    {column.label}
                  </SortableHeaderCell>
                );
              }
              return (
                <FinancialHeaderCell
                  key={column.id}
                  className={twMerge('px-3 py-3', column.headerClassName)}
                  style={{ textAlign: column.align || 'left' }}>
                  {column.label}
                </FinancialHeaderCell>
              );
            })}
          </BaseTableHeaderRow>
        </BaseTableHeader>
        <BaseTableBody className="bg-white">
          {showSpacerRow && <BaseTableSpacerRow />}
          {rows.map((row, index) => (
            <BaseTableDataRow
              key={index}
              className="group cursor-pointer transition-all hover:bg-secondary-200 hover:bg-opacity-50 even:bg-secondary-50 even:bg-opacity-30">
              {renderRow(row, index)}
            </BaseTableDataRow>
          ))}
        </BaseTableBody>
      </BaseTable>
    </div>
  );
}

/**
 * Sortable header cell component for financial tables
 */
export function SortableHeaderCell({
  column,
  sortConfig,
  onSort,
  className,
  children,
  ...props
}: {
  column: string;
  sortConfig: SortConfig;
  onSort: (column: string) => void;
  className?: string;
  children: React.ReactNode;
} & PropsOf<'th'>): JSX.Element {
  const isActive = sortConfig.column === column;
  return (
    <th
      className={twMerge(
        'text-left text-xs font-semibold text-primary-900 tracking-wider cursor-pointer hover:text-primary-300 transition-colors select-none',
        className,
      )}
      onClick={() => onSort(column)}
      {...props}>
      <div className="flex items-center gap-2">
        <span>{children}</span>
        {isActive ? (
          sortConfig.direction === 'asc' ? (
            <HiChevronUp className="w-4 h-4 text-primary-300" />
          ) : (
            <HiChevronDown className="w-4 h-4 text-primary-300" />
          )
        ) : (
          <HiArrowsUpDown className="w-4 h-4 text-primary-300 opacity-50" />
        )}
      </div>
    </th>
  );
}

/**
 * Standard header cell component for financial tables
 */
export function FinancialHeaderCell({ className, ...props }: PropsOf<'th'>): JSX.Element {
  return (
    <th
      className={twMerge(
        'px-3 py-3 text-left text-xs font-semibold text-primary-900 tracking-wider',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Standard data cell component for financial tables
 */
export function FinancialDataCell({ className, ...props }: PropsOf<'td'>): JSX.Element {
  return (
    <td
      className={twMerge('px-3 py-3 text-sm font-medium text-primary-900', className)}
      {...props}
    />
  );
}

/**
 * Compact date display component for financial tables
 * Shows relative dates for recent transactions (Today, Yesterday, day of week)
 * and compact format for older dates
 */
export function CompactDate({ date }: { date: Date }): JSX.Element {
  const today = dayjs();
  const transactionDate = dayjs(date);
  const daysDiff = today.diff(transactionDate, 'day');

  // Show relative dates for recent transactions
  if (daysDiff === 0) {
    return <span className="text-primary-300 font-semibold">Today</span>;
  }
  if (daysDiff === 1) {
    return <span className="text-primary-300">Yesterday</span>;
  }
  if (daysDiff < 7) {
    return <span>{transactionDate.format('ddd')}</span>;
  }

  // For older dates, show compact format: DD MMM (or DD/MM if current year)
  const isCurrentYear = transactionDate.year() === today.year();
  if (isCurrentYear) {
    return <span>{transactionDate.format('D MMM')}</span>;
  }
  return <span>{transactionDate.format('D MMM YY')}</span>;
}
